import { ImageScaleView, ViewOpts } from '@play-co/timestep-core/ui';
import { TextInput } from 'src/game/components/shared/TextInput';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';

interface SearchInputFieldOpts extends ViewOpts {
  fontSize?: string;
  placeholder?: string;
  multiline?: boolean;
  onChange?: (valid: boolean) => void;
  onSubmit?: (text: string) => void;
  onClear?: () => void;
  submitButton?: boolean;
  submitButtonText?: string;
  allowClear?: boolean;
  minLength?: number;
  maxLength?: number;
  inputClass?: string;
}

export class SearchInputField extends ImageScaleView {
  public get text() {
    return this.inputField.text;
  }
  public readonly inputField: TextInput;
  private readonly clearButton?: ButtonScaleView;
  private readonly submitButton?: ButtonScaleViewWithText;
  private readonly options: SearchInputFieldOpts;
  private readonly icon?: ImageScaleView;
  public onChange?: (valid: boolean) => void;
  public onSubmit?: (text: string) => void;
  public onClear?: () => void;

  constructor(opts: SearchInputFieldOpts) {
    super({
      ...opts,
      superview: undefined, // Prevent immediate addition to view hierarchy
      image: 'assets/ui/shared/frames/inset_panel.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 26, right: 26 },
        vertical: { top: 26, bottom: 26 },
      },
      width: opts.width ?? 370,
      height: opts.height ?? 69,
      offsetY: -69 * 0.5,
    });

    this.options = opts;

    const iconSize = this.style.height - 30;
    const width = this.style.width * 0.88 - iconSize * 0.5;
    const height = this.style.height - (opts.multiline ? 40 : 15);
    const paddingX = (this.style.width - width) * 0.5 + iconSize * 0.5;
    const paddingY = (this.style.height - height) * 0.5;
    const placeholder = opts.placeholder;
    const submitWidth = 100;
    const submitHeight = this.style.height - 14;

    let inputFieldWidth = width;
    if (opts.allowClear) inputFieldWidth -= iconSize;
    if (opts.submitButton) inputFieldWidth -= submitWidth;

    let clearButtonX = this.style.width - this.style.height * 0.5;
    if (opts.submitButton) clearButtonX -= submitWidth;

    this.inputField = new TextInput({
      superview: this,
      width: inputFieldWidth,
      height,
      x: paddingX,
      y: paddingY,
      placeholder,
      multiline: opts.multiline,
      minLength: opts.minLength,
      maxLength: opts.maxLength,
      inputClass: opts.inputClass,
    });

    this.icon = new ImageScaleView({
      superview: this,
      width: iconSize,
      height: iconSize,
      centerOnOrigin: true,
      x: this.style.height * 0.5,
      y: this.style.height * 0.5,
      scaleMethod: 'contain',
      image: 'assets/ui/shared/icons/icon_search.png',
    });

    if (opts.allowClear) {
      this.clearButton = new ButtonScaleView({
        superview: this,
        x: clearButtonX,
        y: this.style.height * 0.5,
        image: 'assets/ui/shared/icons/icon_clearinput.png',
        width: iconSize,
        height: iconSize,
        centerOnOrigin: true,
        onClick: async () => {
          sounds.sfx.play('ok');
          this.inputField.text = '';
          this.inputField.validate();
          this.onClear?.();
        },
        tracking: {
          feature: 'search_input_field',
          $subFeature: 'search_input_field_clear',
        },
      });
    }

    if (opts.submitButton) {
      this.submitButton = new ButtonScaleViewWithText({
        superview: this,
        x: this.style.width - submitWidth * 0.5 - 8,
        y: this.style.height * 0.5,
        image: 'assets/ui/shared/buttons/btn_search.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 34, right: 34 },
          vertical: { top: 34, bottom: 34 },
        },
        font: 'Body',
        textColor: '#AE7E4A',
        fontSize: 18,
        pressedOffsetY: 2,
        imgScale: DOWNSCALE_FACTOR,
        width: submitWidth,
        height: submitHeight,
        centerOnOrigin: true,
        localeText: () => opts.submitButtonText ?? i18n('basic.search'),
        onClick: async () => {
          sounds.sfx.play('ok');
          this.submit();
        },
        tracking: {
          feature: 'search_input_field',
          $subFeature: 'search_input_field_search',
        },
      });
    }

    this.onChange = opts.onChange;
    this.onSubmit = opts.onSubmit;
    this.onClear = opts.onClear;
    this.inputField.on('InputValidated', this.handleChange.bind(this));
    this.handleChange();

    if (opts.submitButton) {
      this.addListener('ViewAdded', () => {
        window.addEventListener('keydown', this.handleKeyDown);
      });

      this.addListener('ViewRemoved', () => {
        window.removeEventListener('keydown', this.handleKeyDown);
      });
    }

    /**
     * Add to superview, if provided,
     * after the 'ViewAdded' and 'ViewRemoved' listener is set up
     */
    opts.superview?.addSubview(this);
  }

  private handleChange(valid?: boolean) {
    const value = this.inputField.text;
    this.onChange?.(!!valid);
    this.inputField.updateOpts({
      inputStyle: {
        color: '#FFFFFF',
        textAlign: 'left',
        fontWeight: 'bold',
        fontSize: `${this.options.fontSize ?? `2.5vh`}`,
        font: 'Body',
        resize: 'none',
      },
    });
    this.clearButton?.updateOpts({ visible: !!value.length });
    this.submitButton?.updateOpts({ visible: !!value.length });
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    // Only if the element is focused
    if (document.activeElement !== this.inputField.input) {
      return;
    }
    // Submit when press Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      this.submit();
    }
  };

  private submit() {
    if (!this.onSubmit) return;
    const value = this.inputField.text;
    if (value.length >= (this.options.minLength ?? 1)) {
      this.onSubmit(value);
    } else {
      this.inputField.validate();
    }
  }
}
